// ============================================================
// Olivium Project Portal Factory — data shapes, registries, API wrappers,
// and pure helpers (PHASE_2J).
//
// A project portal is a REAL saved record (sq_project_portals) reached
// through /api/square/command-center/portal-factory/*. This module NEVER
// fabricates a portal, module, action, note, blocker, or receipt — every
// list comes only from the API, and empty / auth-required / error /
// backend-not-migrated states are surfaced honestly.
//
// The module + template registries below are the UI-facing catalog. They
// define recommended module sets and honest per-module status; they do NOT
// fake live integrations or real client/project data. Inherited modules
// (Communications / Team / Daily Meetings) carry a deep-link into the
// existing V2 spine — this factory attaches, it does not rebuild them.
// ============================================================

import { commandCenterPath } from '../../commandCenterRoutes'
import { apiRequest } from '../../../../api/client'

// ---- route roots (kept in lockstep with router.tsx) ----------------------

export const PORTAL_FACTORY_ROOT = commandCenterPath('olivium/portal-factory')
export const PORTAL_CREATE_ROOT = commandCenterPath('olivium/portal-factory/new')
export const PORTAL_REGISTRY_ROOT = commandCenterPath('olivium/portals')
export const OLIVIUM_ACCENT = '#7c3aed'

export function portalDetailPath(portalId: string): string {
  return `${PORTAL_REGISTRY_ROOT}/${portalId}`
}
export function portalModulePath(portalId: string, moduleKey: string): string {
  return `${PORTAL_REGISTRY_ROOT}/${portalId}/modules/${moduleKey}`
}
export function portalContextPath(portalId: string): string {
  return `${PORTAL_REGISTRY_ROOT}/${portalId}/context`
}
export function portalRepoPath(portalId: string): string {
  return `${PORTAL_REGISTRY_ROOT}/${portalId}/repo`
}
export function recipientPreviewPath(portalId: string): string {
  return `${PORTAL_REGISTRY_ROOT}/${portalId}/recipient-preview`
}

// ---- vocabularies (in lockstep with the migration + API helper) ----------

export const PROJECT_TYPES = [
  'internal_project', 'client_project', 'product_project', 'operations_project',
  'legal_project', 'marketing_project', 'event_project', 'custom_project',
] as const
export type ProjectType = (typeof PROJECT_TYPES)[number]

export const PORTAL_TEMPLATE_KEYS = [
  'default_olivium_portal', 'internal_operations_portal', 'client_workspace_portal',
  'product_build_portal', 'legal_document_portal', 'marketing_command_portal',
  'event_command_portal', 'custom_blank_portal',
] as const
export type PortalTemplateKey = (typeof PORTAL_TEMPLATE_KEYS)[number]

export const VISIBILITY_STATUSES = ['private', 'internal', 'shared'] as const
export type VisibilityStatus = (typeof VISIBILITY_STATUSES)[number]

export const READINESS_STATUSES = [
  'planning', 'building', 'in_review', 'blocked', 'ready', 'live', 'archived',
] as const
export type ReadinessStatus = (typeof READINESS_STATUSES)[number]

export const MODULE_STATUS_VALUES = [
  'POWERED', 'SOURCE_READY', 'BACKEND_DEFERRED', 'INTEGRATION_DEFERRED',
  'REALTIME_DEFERRED', 'TEMPLATE',
] as const
export type ModuleStatus = (typeof MODULE_STATUS_VALUES)[number]

export type ActionStatus = 'open' | 'in_progress' | 'blocked' | 'done'
export type ActionPriority = 'low' | 'normal' | 'high' | 'urgent'
export type BlockerSeverity = 'low' | 'medium' | 'high' | 'critical'
export type BlockerStatus = 'open' | 'resolved'

// ---- project-type + readiness metadata (labels only; never fake data) ----

export const PROJECT_TYPE_META: Record<ProjectType, { label: string; blurb: string }> = {
  internal_project: { label: 'Internal', blurb: 'Internal team or platform work.' },
  client_project: { label: 'Client', blurb: 'A client engagement (external access deferred).' },
  product_project: { label: 'Product', blurb: 'A product or feature build.' },
  operations_project: { label: 'Operations', blurb: 'Operational lane or process.' },
  legal_project: { label: 'Legal', blurb: 'Legal / document-driven work.' },
  marketing_project: { label: 'Marketing', blurb: 'Marketing or campaign work.' },
  event_project: { label: 'Event', blurb: 'An event or launch.' },
  custom_project: { label: 'Custom', blurb: 'Anything else — start blank.' },
}

export const READINESS_META: Record<ReadinessStatus, { label: string; accent: string }> = {
  planning: { label: 'Planning', accent: '#8a94a0' },
  building: { label: 'Building', accent: '#2563eb' },
  in_review: { label: 'In review', accent: '#f59e0b' },
  blocked: { label: 'Blocked', accent: '#dc2626' },
  ready: { label: 'Ready', accent: '#059669' },
  live: { label: 'Live', accent: '#14b8a6' },
  archived: { label: 'Archived', accent: '#6a7480' },
}

// ---- module registry — the catalog a portal attaches from -----------------

export type ModuleCategory = 'core' | 'inherited' | 'future'

export interface ModuleCatalogEntry {
  key: string
  label: string
  category: ModuleCategory
  /** The honest status this module carries when attached. */
  status: ModuleStatus
  /** Deep-link into an existing V2 surface (inherited modules only). */
  linkTarget?: string
  purpose: string
  /** Extra honest caveat (e.g. REALTIME_DEFERRED / applies migration X). */
  note?: string
}

export const MODULE_CATALOG: ModuleCatalogEntry[] = [
  // Core — portal-native, POWERED once migration 20260709000003 is applied.
  { key: 'overview', label: 'Overview', category: 'core', status: 'POWERED', purpose: 'Portal summary, readiness, and module status.' },
  { key: 'actions', label: 'Actions', category: 'core', status: 'POWERED', purpose: 'Portal action items — open, in progress, blocked, done.' },
  { key: 'notes', label: 'Notes', category: 'core', status: 'POWERED', purpose: 'The running notes log for this portal.' },
  { key: 'blockers', label: 'Blockers', category: 'core', status: 'POWERED', purpose: 'Flagged blockers with severity and resolution.' },
  { key: 'receipts', label: 'Receipts', category: 'core', status: 'POWERED', purpose: "The portal's real receipt ledger." },
  { key: 'settings', label: 'Settings', category: 'core', status: 'POWERED', purpose: 'Edit portal fields, template, visibility, readiness.' },
  // Tasks reuses the existing V2 Team/Tasks spine rather than a new table.
  {
    key: 'tasks', label: 'Tasks', category: 'inherited', status: 'POWERED',
    linkTarget: commandCenterPath('team'),
    purpose: 'Project tasks and blockers attach to the existing V2 Team/Tasks spine.',
    note: 'Reuses the #104 sq_team_tasks spine (20260709000002 applied 2026-07-10).',
  },
  // Inherited — link to the existing V2 spine; attach, never rebuild.
  {
    key: 'communications', label: 'Communications', category: 'inherited', status: 'POWERED',
    linkTarget: commandCenterPath('communications'),
    purpose: 'Project-scoped channels and DMs attach here.',
    note: 'Reuses the #104 comms spine (20260709000002 applied 2026-07-10). REALTIME_DEFERRED.',
  },
  {
    key: 'team', label: 'Team', category: 'inherited', status: 'POWERED',
    linkTarget: commandCenterPath('team'),
    purpose: 'Project roles, members, and ownership attach here.',
    note: 'Reuses the #104 team spine (20260709000002 applied 2026-07-10).',
  },
  {
    key: 'daily_meetings', label: 'Daily Meetings', category: 'inherited', status: 'POWERED',
    linkTarget: commandCenterPath('operations/daily-meetings'),
    purpose: 'Daily meeting records for this project.',
    note: 'Reuses the #101 sq_daily_meetings workflow (20260709000001 applied 2026-07-10).',
  },
  // Future — deferred, not yet attachable to a real backend.
  { key: 'files', label: 'Files', category: 'future', status: 'BACKEND_DEFERRED', purpose: 'Project files and deliverables.', note: 'Not built in v1.' },
  { key: 'client_access', label: 'Client Access', category: 'future', status: 'INTEGRATION_DEFERRED', purpose: 'External client portal access.', note: 'External client access is deferred.' },
  { key: 'documents', label: 'Documents', category: 'future', status: 'BACKEND_DEFERRED', purpose: 'Document workspaces.', note: 'Not built in v1.' },
  { key: 'approvals', label: 'Approvals', category: 'future', status: 'BACKEND_DEFERRED', purpose: 'Approval gates.', note: 'Not built in v1.' },
  { key: 'intake', label: 'Intake', category: 'future', status: 'BACKEND_DEFERRED', purpose: 'Client / project intake.', note: 'Not built in v1.' },
  { key: 'campaigns', label: 'Campaigns', category: 'future', status: 'BACKEND_DEFERRED', purpose: 'Marketing campaigns.', note: 'Not built in v1.' },
]

export const MODULE_CATALOG_BY_KEY: Record<string, ModuleCatalogEntry> =
  Object.fromEntries(MODULE_CATALOG.map((m) => [m.key, m]))

export function moduleCatalogEntry(key: string): ModuleCatalogEntry | undefined {
  return MODULE_CATALOG_BY_KEY[key]
}

// ---- template registry — recommended module sets + copy -------------------

export interface PortalTemplate {
  key: PortalTemplateKey
  label: string
  description: string
  recommendedModules: string[]
}

export const PORTAL_TEMPLATES: PortalTemplate[] = [
  { key: 'default_olivium_portal', label: 'Default Olivium Portal', description: 'The baseline trust shell — overview, actions, notes, blockers, receipts.', recommendedModules: ['overview', 'actions', 'notes', 'blockers', 'receipts', 'settings'] },
  { key: 'internal_operations_portal', label: 'Internal Operations', description: 'Operational lane with tasks and daily meetings.', recommendedModules: ['overview', 'actions', 'tasks', 'notes', 'blockers', 'daily_meetings', 'receipts', 'settings'] },
  { key: 'client_workspace_portal', label: 'Client Workspace', description: 'Client engagement shell with comms and team (external access deferred).', recommendedModules: ['overview', 'communications', 'team', 'actions', 'notes', 'receipts', 'settings'] },
  { key: 'product_build_portal', label: 'Product Build', description: 'Feature/product build with tasks and blockers.', recommendedModules: ['overview', 'actions', 'tasks', 'blockers', 'notes', 'receipts', 'settings'] },
  { key: 'legal_document_portal', label: 'Legal / Document', description: 'Document-driven legal work. Documents module is deferred.', recommendedModules: ['overview', 'notes', 'blockers', 'receipts', 'settings'] },
  { key: 'marketing_command_portal', label: 'Marketing Command', description: 'Marketing lane with comms. Campaigns module is deferred.', recommendedModules: ['overview', 'communications', 'actions', 'notes', 'receipts', 'settings'] },
  { key: 'event_command_portal', label: 'Event Command', description: 'Event/launch shell with team and comms.', recommendedModules: ['overview', 'team', 'communications', 'actions', 'notes', 'receipts', 'settings'] },
  { key: 'custom_blank_portal', label: 'Custom Blank', description: 'Start blank — overview + settings only.', recommendedModules: ['overview', 'settings'] },
]

export const PORTAL_TEMPLATE_BY_KEY: Record<string, PortalTemplate> =
  Object.fromEntries(PORTAL_TEMPLATES.map((t) => [t.key, t]))

export function portalTemplate(key: string): PortalTemplate | undefined {
  return PORTAL_TEMPLATE_BY_KEY[key]
}

// ---- design + rendering rules (the shared-renderer contract) --------------
//
// The factory is metadata-driven: every surface renders from MODULE_CATALOG /
// PORTAL_TEMPLATES / READINESS_META / the client-visibility rules below through
// the shared PortalKit primitives (SectionCard, StateBlock, ModuleStatusBadge,
// ReadinessPill) on CommandCenterShell — never a one-off guessed layout per portal.

export const PORTAL_DESIGN_RULES = [
  'Every surface renders on the shared CommandCenterShell chrome via the PortalKit primitives — no per-portal bespoke layout.',
  'Modules render from MODULE_CATALOG metadata (label, category, status, purpose, link_target); the shell never invents a module.',
  'Templates render from PORTAL_TEMPLATES metadata (recommended module set + copy); template content is labelled TEMPLATE.',
  'Status + readiness always render through ModuleStatusBadge / ReadinessPill so the vocabulary is consistent and honest.',
  'Backend-not-migrated, auth-required, loading, and empty states are honest StateBlocks — never fabricated data.',
] as const

// ---- client-visibility rules (external access is DEFERRED) ----------------
//
// v1 posture: NOTHING is client-visible by default. External client access is
// a deferred, approval-gated capability — until it lands, no operator-internal
// record (DM, note, blocker, draft) is ever exposed to a client. This registry
// makes that posture explicit and testable; the Client Access module is
// INTEGRATION_DEFERRED and the portal visibility vocabulary has no external/
// public value.

export const CLIENT_ACCESS_POSTURE = 'deferred' as const

export interface ClientVisibilityRule {
  kind: string
  label: string
  /** v1 invariant: always false — nothing is client-visible by default. */
  clientVisibleByDefault: false
  rule: string
}

export const CLIENT_VISIBILITY_RULES: ClientVisibilityRule[] = [
  { kind: 'internal_dms', label: 'Internal DMs', clientVisibleByDefault: false, rule: 'Direct messages are operator-internal and never client-visible. Client-facing channels would be a separate, approval-gated capability — deferred.' },
  { kind: 'private_notes', label: 'Private team notes', clientVisibleByDefault: false, rule: 'The portal notes log is an internal operator record — not client-visible.' },
  { kind: 'raw_blockers', label: 'Raw blockers', clientVisibleByDefault: false, rule: 'Blockers are internal. A client never sees raw blockers; publishing a curated status would be an explicit deferred step.' },
  { kind: 'unapproved_drafts', label: 'Unapproved drafts', clientVisibleByDefault: false, rule: 'Drafts are never client-visible. There is no implicit publish — a client-visible update would require an explicit approval/publish action, which is deferred.' },
  { kind: 'client_updates', label: 'Client-visible updates', clientVisibleByDefault: false, rule: 'A client-visible update requires an explicit approval/publish action. External client access is deferred across the factory, so nothing is published to a client yet.' },
]

/** v1 invariant: nothing is client-visible by default (external access deferred). */
export function isClientVisibleByDefault(_kind: string): boolean {
  return false
}

// ---- record shapes --------------------------------------------------------

export interface PortalCounts { modules: number; open_actions: number; open_blockers: number; receipts: number }

export interface PortalRecord {
  id: string
  project_name: string
  project_slug: string
  project_type: ProjectType
  owner_label: string | null
  visibility_status: VisibilityStatus
  portal_template: PortalTemplateKey
  readiness_status: ReadinessStatus
  summary: string | null
  metadata: Record<string, unknown>
  /** Canonical project this portal is bound to (sq_projects.id), or null when standalone. */
  project_id: string | null
  created_by: string
  created_at: string
  updated_at: string
  counts?: PortalCounts
}

export interface PortalModuleRecord {
  id: string
  portal_id: string
  module_key: string
  module_label: string
  module_status: ModuleStatus
  link_target: string | null
  enabled: boolean
  config: Record<string, unknown>
  created_by: string
  created_at: string
  updated_at: string
}

export interface PortalActionRecord {
  id: string; portal_id: string; module_key: string | null; title: string; detail: string | null
  status: ActionStatus; priority: ActionPriority; created_by: string; created_at: string; updated_at: string
}
export interface PortalNoteRecord {
  id: string; portal_id: string; module_key: string | null; body: string; created_by: string; created_at: string
}
export interface PortalBlockerRecord {
  id: string; portal_id: string; module_key: string | null; summary: string; severity: BlockerSeverity
  status: BlockerStatus; resolution: string | null; resolved_at: string | null; created_by: string; created_at: string; updated_at: string
}
export interface PortalReceiptRecord {
  id: string; portal_id: string; module_key: string | null; receipt_type: string; summary: string
  detail: Record<string, unknown>; created_by: string; created_at: string
}

/** The canonical project this portal is bound to (identity only), or null. */
export interface LinkedProject {
  id: string
  name: string
  slug: string
  status: string
}

export interface PortalDetail {
  portal: PortalRecord
  /** Canonical project identity via the FK (sq_projects), null when standalone. */
  linked_project: LinkedProject | null
  modules: PortalModuleRecord[]
  actions: PortalActionRecord[]
  notes: PortalNoteRecord[]
  blockers: PortalBlockerRecord[]
  receipts: PortalReceiptRecord[]
}

/** The mutable fields the create form collects. */
export interface PortalDraft {
  project_name: string
  project_slug: string
  project_type: ProjectType
  owner_label: string
  visibility_status: VisibilityStatus
  portal_template: PortalTemplateKey
  readiness_status: ReadinessStatus
  summary: string
  selected_modules: string[]
  /** Optional canonical project to bind the portal to (owner-checked server-side). */
  project_id?: string | null
}

// ---- pure helpers (no I/O, unit-testable) ---------------------------------

/** Slugify a name the same way the API does (kept in sync). */
export function slugify(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80).replace(/-+$/g, '')
}

/** A blank draft seeded from the default template's recommended modules. */
export function emptyPortalDraft(): PortalDraft {
  return {
    project_name: '', project_slug: '', project_type: 'internal_project', owner_label: '',
    visibility_status: 'private', portal_template: 'default_olivium_portal', readiness_status: 'planning',
    summary: '', selected_modules: [...(portalTemplate('default_olivium_portal')?.recommendedModules ?? [])],
  }
}

/** Expand selected module keys into API descriptors from the catalog. */
export function draftModuleDescriptors(keys: string[]) {
  const seen = new Set<string>()
  const out: Array<{ module_key: string; module_label: string; module_status: ModuleStatus; link_target: string | null }> = []
  for (const key of keys) {
    const entry = moduleCatalogEntry(key)
    if (!entry || seen.has(entry.key)) continue
    seen.add(entry.key)
    out.push({ module_key: entry.key, module_label: entry.label, module_status: entry.status, link_target: entry.linkTarget ?? null })
  }
  return out
}

/** Derived, honest readiness summary for a portal detail — never fabricated. */
export function summarizePortal(detail: PortalDetail) {
  const openActions = detail.actions.filter((a) => a.status !== 'done').length
  const openBlockers = detail.blockers.filter((b) => b.status === 'open').length
  return {
    modules: detail.modules.filter((m) => m.enabled).length,
    openActions,
    doneActions: detail.actions.filter((a) => a.status === 'done').length,
    openBlockers,
    notes: detail.notes.length,
    receipts: detail.receipts.length,
  }
}

/** The next recommended build step — derived from real state, honest. */
export function nextBuildStep(detail: PortalDetail): string {
  if (detail.modules.filter((m) => m.enabled).length === 0) return 'Attach at least one module to this portal.'
  const openBlockers = detail.blockers.filter((b) => b.status === 'open')
  if (openBlockers.length) return `Resolve ${openBlockers.length} open blocker(s) before advancing readiness.`
  const openActions = detail.actions.filter((a) => a.status !== 'done')
  if (openActions.length) return `Work the ${openActions.length} open action(s).`
  if (detail.portal.readiness_status === 'planning') return 'Move readiness to Building once work starts.'
  return 'Portal is on track — keep the receipt ledger current.'
}

// ---- API wrappers (browser-only; apiRequest attaches the bearer token) ----

const BASE = '/api/square/command-center/portal-factory'

export async function listPortals(signal?: AbortSignal): Promise<PortalRecord[]> {
  const res = await apiRequest<{ ok: true; portals: PortalRecord[] }>(`${BASE}/portals`, { method: 'GET', signal })
  return res.portals || []
}

export interface CreatePortalResult {
  portal: PortalRecord
  modulesAttached: number
  receiptSummary: string | null
}

export async function createPortal(draft: PortalDraft): Promise<CreatePortalResult> {
  const res = await apiRequest<{ ok: true; portal: PortalRecord; modules_attached?: number; receipt?: { summary?: string } | null }>(`${BASE}/portals`, {
    method: 'POST',
    body: {
      project_name: draft.project_name,
      project_slug: draft.project_slug || slugify(draft.project_name),
      project_type: draft.project_type,
      owner_label: draft.owner_label,
      visibility_status: draft.visibility_status,
      portal_template: draft.portal_template,
      readiness_status: draft.readiness_status,
      summary: draft.summary,
      // Only sent when the caller supplies it; the server authorizes ownership.
      ...(draft.project_id !== undefined ? { project_id: draft.project_id } : {}),
      selected_modules: draftModuleDescriptors(draft.selected_modules),
    },
  })
  return {
    portal: res.portal,
    modulesAttached: typeof res.modules_attached === 'number' ? res.modules_attached : 0,
    receiptSummary: res.receipt?.summary ?? null,
  }
}

export async function updatePortal(id: string, patch: Partial<Omit<PortalDraft, 'selected_modules'>>): Promise<PortalRecord> {
  const res = await apiRequest<{ ok: true; portal: PortalRecord }>(`${BASE}/portals`, {
    method: 'POST', body: { action: 'update', id, ...patch },
  })
  return res.portal
}

export async function getPortal(id: string, signal?: AbortSignal): Promise<PortalDetail> {
  const res = await apiRequest<{ ok: true } & Omit<PortalDetail, never>>(`${BASE}/portal`, { method: 'GET', query: { id }, signal })
  return { portal: (res as any).portal, linked_project: (res as any).linked_project ?? null, modules: (res as any).modules || [], actions: (res as any).actions || [], notes: (res as any).notes || [], blockers: (res as any).blockers || [], receipts: (res as any).receipts || [] }
}

/** Link the portal to (or, with null, unlink it from) a canonical project.
 *  Ownership is enforced server-side; a cross-owner link is refused (403). */
export async function setPortalProjectLink(id: string, project_id: string | null): Promise<PortalRecord> {
  return updatePortal(id, { project_id })
}

/** Publish (or unpublish) ONE module to the recipient portal. The flag lives
 *  in the module's config jsonb (`client_published`); nothing is ever
 *  auto-published, and the server replaces config wholesale, so the caller
 *  passes the module's CURRENT config to preserve its other keys. */
export async function setModulePublished(
  portal_id: string,
  module: { id: string; config: Record<string, unknown> | null },
  published: boolean,
): Promise<PortalModuleRecord> {
  return updateModule(portal_id, module.id, {
    config: { ...(module.config ?? {}), client_published: published },
  })
}

/** The owner's Preview-as-Recipient manifest — the SAME shape the recipient
 *  session serves, marked preview:true. No session or invite is created. */
export async function getRecipientPreview(portal_id: string, signal?: AbortSignal) {
  return apiRequest<{
    ok: boolean
    state: string
    manifest?: import('../../../../api/client').RecipientManifest
  }>(`${BASE}/recipient-preview`, { method: 'GET', query: { portal_id }, signal })
}

export async function attachModule(portal_id: string, key: string): Promise<PortalModuleRecord> {
  const entry = moduleCatalogEntry(key)
  const res = await apiRequest<{ ok: true; module: PortalModuleRecord }>(`${BASE}/modules`, {
    method: 'POST',
    body: {
      portal_id,
      module_key: key,
      module_label: entry?.label ?? key,
      module_status: entry?.status ?? 'SOURCE_READY',
      link_target: entry?.linkTarget ?? null,
    },
  })
  return res.module
}

export async function updateModule(portal_id: string, id: string, patch: { module_status?: ModuleStatus; enabled?: boolean; config?: Record<string, unknown> }): Promise<PortalModuleRecord> {
  const res = await apiRequest<{ ok: true; module: PortalModuleRecord }>(`${BASE}/modules`, {
    method: 'POST', body: { action: 'update', portal_id, id, ...patch },
  })
  return res.module
}

export async function createAction(portal_id: string, input: { title: string; detail?: string; priority?: ActionPriority; module_key?: string | null }): Promise<PortalActionRecord> {
  const res = await apiRequest<{ ok: true; action: PortalActionRecord }>(`${BASE}/actions`, { method: 'POST', body: { portal_id, ...input } })
  return res.action
}
export async function updateAction(portal_id: string, id: string, patch: { status?: ActionStatus; priority?: ActionPriority; title?: string; detail?: string }): Promise<PortalActionRecord> {
  const res = await apiRequest<{ ok: true; action: PortalActionRecord }>(`${BASE}/actions`, { method: 'POST', body: { action: 'update', portal_id, id, ...patch } })
  return res.action
}
export async function createNote(portal_id: string, body: string, module_key?: string | null): Promise<PortalNoteRecord> {
  const res = await apiRequest<{ ok: true; note: PortalNoteRecord }>(`${BASE}/notes`, { method: 'POST', body: { portal_id, body, module_key } })
  return res.note
}
export async function createBlocker(portal_id: string, input: { summary: string; severity?: BlockerSeverity; module_key?: string | null }): Promise<PortalBlockerRecord> {
  const res = await apiRequest<{ ok: true; blocker: PortalBlockerRecord }>(`${BASE}/blockers`, { method: 'POST', body: { portal_id, ...input } })
  return res.blocker
}
export async function updateBlocker(portal_id: string, id: string, patch: { status?: BlockerStatus; severity?: BlockerSeverity; resolution?: string; summary?: string }): Promise<PortalBlockerRecord> {
  const res = await apiRequest<{ ok: true; blocker: PortalBlockerRecord }>(`${BASE}/blockers`, { method: 'POST', body: { action: 'update', portal_id, id, ...patch } })
  return res.blocker
}
export async function createReceipt(portal_id: string, summary: string, module_key?: string | null): Promise<PortalReceiptRecord> {
  const res = await apiRequest<{ ok: true; receipt: PortalReceiptRecord }>(`${BASE}/receipts`, { method: 'POST', body: { portal_id, summary, module_key } })
  return res.receipt
}

// ---- project context + repo spine (PHASE_2L) -----------------------------

export const REPO_PROVIDERS = ['github', 'gitlab', 'bitbucket', 'other'] as const
export type RepoProvider = (typeof REPO_PROVIDERS)[number]

export interface PortalContextRecord {
  id: string
  portal_id: string
  repo_provider: RepoProvider | null
  repo_owner: string | null
  repo_name: string | null
  repo_url: string | null
  default_branch: string | null
  active_branch: string | null
  current_pr_url: string | null
  deployment_url: string | null
  vercel_project_name: string | null
  supabase_project_ref: string | null
  next_action: string | null
  sync_status: 'INTEGRATION_DEFERRED' | 'manual' | 'synced'
  github_permission_status: 'GITHUB_PERMISSION_DEFERRED' | 'requested' | 'granted' | 'revoked'
  client_visible: boolean
  published_at: string | null
  metadata: Record<string, unknown>
  created_by: string
  created_at: string
  updated_at: string
}

/** The editable repo/deployment fields (never sync/permission/publish — those
 *  are separate honest actions). */
export type PortalContextDraft = Partial<Pick<PortalContextRecord,
  'repo_provider' | 'repo_owner' | 'repo_name' | 'repo_url' | 'default_branch' | 'active_branch'
  | 'current_pr_url' | 'deployment_url' | 'vercel_project_name' | 'supabase_project_ref' | 'next_action'>>

/** The repo/deploy field catalog — drives the /repo + /context forms. */
export const CONTEXT_FIELDS: Array<{ key: keyof PortalContextDraft; label: string; placeholder: string; group: 'repo' | 'deploy' }> = [
  { key: 'repo_owner', label: 'Repo owner', placeholder: 'Quam-Code', group: 'repo' },
  { key: 'repo_name', label: 'Repo name', placeholder: 'square-code', group: 'repo' },
  { key: 'repo_url', label: 'Repo URL', placeholder: 'https://github.com/Quam-Code/square-code', group: 'repo' },
  { key: 'default_branch', label: 'Default branch', placeholder: 'main', group: 'repo' },
  { key: 'active_branch', label: 'Active branch', placeholder: 'feat/…', group: 'repo' },
  { key: 'current_pr_url', label: 'Current PR URL', placeholder: 'https://github.com/…/pull/107', group: 'repo' },
  { key: 'deployment_url', label: 'Deployment URL', placeholder: 'https://square-code-nu.vercel.app', group: 'deploy' },
  { key: 'vercel_project_name', label: 'Vercel project', placeholder: 'square-code', group: 'deploy' },
  { key: 'supabase_project_ref', label: 'Supabase project ref (optional)', placeholder: 'abcd… (ref only, never a key)', group: 'deploy' },
]

export async function getPortalContext(portal_id: string, signal?: AbortSignal): Promise<PortalContextRecord | null> {
  const res = await apiRequest<{ ok: true; context: PortalContextRecord | null }>(`${BASE}/context`, { method: 'GET', query: { portal_id }, signal })
  return res.context
}
export async function upsertPortalContext(portal_id: string, fields: PortalContextDraft): Promise<PortalContextRecord> {
  const res = await apiRequest<{ ok: true; context: PortalContextRecord }>(`${BASE}/context`, { method: 'POST', body: { portal_id, ...fields } })
  return res.context
}
export async function setPortalClientVisible(portal_id: string, publish: boolean): Promise<PortalContextRecord> {
  const res = await apiRequest<{ ok: true; context: PortalContextRecord }>(`${BASE}/context`, { method: 'POST', body: { portal_id, action: publish ? 'publish' : 'unpublish' } })
  return res.context
}

/** Turn any thrown API failure into an honest, actionable message. */
export function humanizePortalError(err: unknown): string {
  const anyErr = err as { message?: string; code?: string }
  const msg = anyErr?.message || anyErr?.code || ''
  if (/relation .* does not exist/i.test(msg) || /could not find the table/i.test(msg) || /list_failed/i.test(anyErr?.code || '') || /schema cache/i.test(msg)) {
    // Name the migration that actually creates the missing table. The
    // sq_project_portal_context table (PHASE_2L) ships in 20260710000001; the
    // portal-factory tables in 20260709000003. The PostgREST error carries the
    // table name, so we point the operator at the right migration (avoids a
    // dead-end "re-apply the migration you already have").
    const migration = /sq_project_portal_context/i.test(msg) ? '20260710000001' : '20260709000003'
    return `The Portal Factory backend is not migrated yet (apply migration ${migration}).`
  }
  return msg || 'Could not reach the Portal Factory backend.'
}
